import { encodeCotizacionId } from "@/utils/hashids";

const resolvedApiBaseUrl =
  import.meta.env.VITE_API_BASE_URL || "https://fb.grupokc.com.mx";

let apiBaseUrl: string = resolvedApiBaseUrl;
let agentId: string | null = null;

/** URL base donde se sirve el widget (para recursos estáticos como el PDF). Si no se setea, se usa la variable de build VITE_WIDGET_PUBLIC_URL o ruta relativa. */
let widgetPublicBaseUrl: string = import.meta.env.VITE_WIDGET_PUBLIC_URL ?? "";

/** URL base para el servicio de reportes (PDF). Email puede usar VITE_BACKEND_URL. */
let reportsBaseUrl: string =
  import.meta.env.VITE_REPORTS_BASE_URL ?? "https://apis.gokc.net";

/**
 * Host de cotización y emisión por aseguradora (Qualitas/ANA Cotiza + Emitir + Nf_File).
 * Vercel: COTIZA_EMISION_API_BASE_URL (sin prefijo; mapeada en vite.config).
 * Configurable: el host definitivo aún puede cambiar.
 */
const DEFAULT_COTIZA_EMIT_BASE_URL = "https://fbc.grupokc.com.mx";
let cotizaEmitBaseUrl: string =
  import.meta.env.VITE_COTIZA_EMISION_API_BASE_URL?.trim() ||
  DEFAULT_COTIZA_EMIT_BASE_URL;

/** Host legacy de email/documentos hasta migrar a apis.gokc.net. */
const LEGACY_EMAIL_BASE_URL = "https://reports.kcapis.net";

export const getApiBaseUrl = (): string => apiBaseUrl;

export const setApiBaseUrl = (url: string): void => {
  apiBaseUrl = url;
};

export const getReportsBaseUrl = (): string => reportsBaseUrl;

export const setReportsBaseUrl = (url: string): void => {
  reportsBaseUrl = url;
};

/** Base URL cotización / emisión aseguradoras (sin slash final). */
export const getCotizaEmitBaseUrl = (): string =>
  cotizaEmitBaseUrl.replace(/\/$/, "");

export const setCotizaEmitBaseUrl = (url: string): void => {
  const trimmed = url.trim().replace(/\/$/, "");
  cotizaEmitBaseUrl = trimmed || DEFAULT_COTIZA_EMIT_BASE_URL;
};

/** POST cotización Qualitas o ANA. */
export const getInsurerCotizaUrl = (
  company: "QUALITAS" | "ANA",
): string => {
  const base = getCotizaEmitBaseUrl();
  return company === "ANA"
    ? `${base}/api/Ana/Cotiza`
    : `${base}/api/Qualitas/Cotiza`;
};

/** POST emisión Qualitas o ANA. */
export const getInsurerEmitUrl = (
  company: "QUALITAS" | "ANA",
): string => {
  const base = getCotizaEmitBaseUrl();
  return company === "ANA"
    ? `${base}/api/Ana/Emitir`
    : `${base}/api/qualitas/Emitir`;
};

/** GET descarga documento por Guid (misma base que cotiza/emisión). */
export const getInsurerDocumentDownloadUrl = (
  guidDocumento: string,
): string => {
  return `${getCotizaEmitBaseUrl()}/api/Nf_File/getFile/?Id_Documento=${guidDocumento}`;
};

/**
 * PDF de cotización (auth por headers partner, no ?token=):
 * GET /reports/autos/cotizaciones/{hashids(id)}/pdf[?formaPago][&paquete][&cia]
 * Headers: x-partner-id / x-partner-apikey (ver partnerAuth).
 * formaPago / paquete / cia son overrides opcionales; sin ellos reports usa el detalle.
 */
export const getQuotePdfDownloadUrl = (params: {
  idCotizacion: number | string;
  formaPago?: string;
  paquete?: string;
  cia?: number | null;
}): string => {
  const hash = encodeCotizacionId(params.idCotizacion);
  const search = new URLSearchParams();

  if (params.formaPago) search.set("formaPago", params.formaPago);
  if (params.paquete) search.set("paquete", params.paquete);
  if (params.cia != null) search.set("cia", String(params.cia));

  const query = search.toString();
  const base = `${getReportsBaseUrl()}/reports/autos/cotizaciones/${hash}/pdf`;
  return query ? `${base}?${query}` : base;
};

/** PDF de póliza: GET /reports/autos/download/pdf/:idPoliza (headers partner, ver partnerAuth). */
export const getPolicyPdfDownloadUrl = (idPoliza: number | string): string => {
  return `${getReportsBaseUrl()}/reports/autos/download/pdf/${idPoliza}`;
};

/** URL base notify (correo cotización). Mismo host que reportes por defecto. */
export const getNotifyBaseUrl = (): string => {
  return import.meta.env.VITE_NOTIFY_BASE_URL ?? getReportsBaseUrl();
};

/** POST notify cotización por email / WhatsApp (`canales`). */
export const getQuoteNotifyUrl = (): string => {
  return `${getNotifyBaseUrl()}/notify/seguros/autos/cotizacion`;
};

/** Backend legacy de descarga de documentos (PDF email attachments). */
export const getEmailBackendBaseUrl = (): string => {
  return import.meta.env.VITE_BACKEND_URL ?? LEGACY_EMAIL_BASE_URL;
};

export const getAgentId = (): string | null => agentId;

export const setAgentId = (id: string | null): void => {
  agentId = id;
};

export const getWidgetPublicBaseUrl = (): string => widgetPublicBaseUrl;

export const setWidgetPublicBaseUrl = (url: string): void => {
  widgetPublicBaseUrl = url;
};

/**
 * Devuelve la URL absoluta del PDF de aviso de privacidad.
 */
export const getPrivacyPdfUrl = (): string => {
  return "https://r2.kc-itservices.net/docs/aviso-privacidad-fiizy.pdf";
};
